// FILE: src/pages/attendance/Attendance.jsx
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { useGlobalContext } from "../../GlobalContext";
import useAttendance from "../../Sevices/useAttendance";
import "./attendance.css";
import { useNavigate } from "react-router-dom";
import {
  FileDown,
  RefreshCcw,
  X,
  Clock3,
  CalendarDays,
  ChevronDown,
} from "lucide-react";
import { exportAnyJsonToExcel } from "../Utils/exportAnyJsonToExcel";
import useDriver from "../../Sevices/Driver Api/useDriver";
const normalizeStatus = (s) => {
  if (!s) return "pending";
  const v = String(s).toLowerCase().trim();
  if (v === "f") return "full_day";
  if (v === "h") return "half_day";
  if (v === "a") return "absent";
  if (v === "p") return "pending";
  if (v.includes("full")) return "full_day";
  if (v.includes("half")) return "half_day";
  if (v.includes("absent")) return "absent";
  return "pending";
};
const STATUS_ORDER = ["pending", "half_day", "full_day", "absent"];
const ROW_HEIGHT = 56;
const OVERSCAN = 7;
const EMPTY_DIRTY = Object.freeze({});
const STATUS_LABEL = {
  full_day: "F",
  half_day: "H",
  absent: "A",
  pending: "PN",
};
const STATUS_CLASS = {
  full_day: "bg-green-500 text-white",
  half_day: "bg-yellow-400 text-black",
  absent: "bg-red-500 text-white",
  pending: "bg-blue-500 text-white",
};
const TYPE_BADGE = {
  full_time: "bg-indigo-500/10 text-indigo-700 border-indigo-200",
  part_time: "bg-sky-500/10 text-sky-700 border-sky-200",
  normal: "bg-slate-500/10 text-slate-700 border-slate-200",
};
const formatDateLabel = (ymd) => {
  if (!ymd) return "No date";
  try {
    return new Date(ymd).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return ymd;
  }
};
const formatDateTimeLabel = (value) => {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  } catch {
    return value;
  }
};
const toYMD = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate(),
  ).padStart(2, "0")}`;
const getMonthDays = (year, month) => {
  const days = new Date(year, month + 1, 0).getDate();
  return Array.from({ length: days }, (_, i) =>
    toYMD(new Date(year, month, i + 1)),
  );
};
const formatDay = (ymd) => new Date(ymd).getDate();
const formatDayName = (ymd) => {
  return new Date(ymd).toLocaleDateString("en-US", {
    weekday: "short",
  });
};

// One lightweight table row per visible driver. Other rows do not rerender
// when one driver's attendance is edited.
const AttendanceDriverRow = React.memo(function AttendanceDriverRow({
  driver,
  dates,
  changes,
  onEdit,
  onHover,
  onLeave,
  onNavigate,
}) {
  return (
    <tr className="attendance-driver-row hover:bg-slate-50/70">
      <td className="attendance-td sticky-driver">
        <div className="min-w-0">
          <button
            type="button"
            onClick={() => onNavigate(driver)}
            className="block w-full max-w-[160px] truncate text-left text-[12px] font-semibold text-blue-600 hover:underline"
            title={driver.name || driver.driverCode}
          >
            {driver.name || "-"}
          </button>
          <div className="text-[10px] text-slate-500">{driver.driverCode}</div>
          <span
            className={[
              "inline-flex items-center rounded-md border px-2 py-0.5 text-[10px] font-semibold",
              TYPE_BADGE[String(driver.type || "").toLowerCase()] ||
                TYPE_BADGE.normal,
            ].join(" ")}
          >
            {String(driver.type || "normal").replaceAll("_", " ")}
          </span>
        </div>
      </td>
      {dates.map((date) => {
        const original = normalizeStatus(driver.attendanceMap?.[date]);
        const status = changes[date] ?? original;
        const isDirty = Object.prototype.hasOwnProperty.call(changes, date);
        const details = driver.attendanceDetailsMap?.[date] || {
          date,
          status: original,
          totalDutyHours: "0h 0m",
          timings: [],
        };

        return (
          <td
            key={date}
            className="attendance-td attendance-date-cell text-center"
          >
            <div className="attendance-cell-wrap">
              <button
                type="button"
                data-attendance-badge="true"
                aria-label={`${driver.name || driver.driverCode}, ${date}, ${status.replaceAll("_", " ")}. Change attendance`}
                onClick={() => onEdit(driver.driverCode, date, original)}
                onMouseEnter={(event) => onHover(event, details, status)}
                onMouseLeave={onLeave}
                className={[
                  "att-badge",
                  STATUS_CLASS[status],
                  isDirty ? "ring-2 ring-slate-900/25" : "",
                ].join(" ")}
                title="Click to change attendance status"
              >
                {STATUS_LABEL[status]}
              </button>
            </div>
          </td>
        );
      })}
    </tr>
  );
});



function getPopupPosition(element, width = 264) {
  const rect = element.getBoundingClientRect();

  const gap = 8;
  const margin = 8;

  const viewportWidth = window.innerWidth;

  // Center popup directly below hovered badge
  let left = rect.left + rect.width / 2 - width / 2;

  // Prevent popup from going outside left/right screen edges
  left = Math.max(margin, Math.min(left, viewportWidth - width - margin));

  // Always open below the hovered badge
  const top = rect.bottom + gap;

  return {
    position: "fixed",
    top,
    left,
  };
}

// One hover detail for the entire grid instead of thousands of hidden cards.
function AttendanceHoverDetails({ popup }) {
  if (!popup) return null;
  const { details, status, position } = popup;
  const timings = Array.isArray(details?.timings) ? details.timings : [];
  return createPortal(
    <div
      className="attendance-floating-details"
      style={position}
      aria-hidden="true"
    >
      <div className="attendance-hover-head">
        <div className="flex items-center gap-2 font-semibold text-slate-800">
          <CalendarDays size={14} /> {formatDateLabel(details?.date)}
        </div>
        <span
          className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${STATUS_CLASS[status]}`}
        >
          {STATUS_LABEL[status]}
        </span>
      </div>
      <div className="attendance-hover-meta">
        <div className="attendance-hover-line">
          <Clock3 size={13} /> Total Hours: {details?.totalDutyHours || "0h 0m"}
        </div>
        <div className="attendance-hover-line">
          <Clock3 size={13} /> Sessions: {timings.length}
        </div>
      </div>
      <div className="attendance-hover-timings">
        {timings.length === 0 ? (
          <div className="attendance-empty-note">No duty timings available</div>
        ) : (
          timings.map((t, index) => (
            <div key={index} className="attendance-timing-item">
              <div className="attendance-timing-title">Session {index + 1}</div>
              <div className="attendance-hover-line">
                <b>On:</b> {formatDateTimeLabel(t.on_duty_time)}
              </div>
              <div className="attendance-hover-line">
                <b>Off:</b> {formatDateTimeLabel(t.off_duty_time)}
              </div>
              <div className="attendance-hover-line">
                <b>Duration:</b> {t.duration || "—"}
              </div>
            </div>
          ))
        )}
      </div>
    </div>,
    document.body,
  );
}

export default function Attendance() {
  const { AdminId } = useGlobalContext();
  const { fetchAttendanceApi, updateAttendanceApi } = useAttendance();
  const { loading, fetchDrivers } = useDriver();
  // month selection
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth()); // 0-index
  const dates = useMemo(() => getMonthDays(year, month), [year, month]);
  // drivers list (for dropdown)
  const [driverOptions, setDriverOptions] = useState([]);
  const [driverListLoading, setDriverListLoading] = useState(false);
  // UI inputs (not applied until Apply Filters)
  const [uiDriverCode, setUiDriverCode] = useState("");
  const [uiSearch, setUiSearch] = useState("");
  const [uiType, setUiType] = useState("all");
  const [uiStatus, setUiStatus] = useState("all");
  const [driverDropdownOpen, setDriverDropdownOpen] = useState(false);
  const [driverSearch, setDriverSearch] = useState("");
  const selectedDriver = driverOptions.find((d) => d.driverId === uiDriverCode);
  const filteredDriverOptions = driverOptions.filter((d) => {
    const search = driverSearch.toLowerCase();
    return (
      String(d.fullName || "")
        .toLowerCase()
        .includes(search) ||
      String(d.username || "")
        .toLowerCase()
        .includes(search) ||
      String(d.driverId || "")
        .toLowerCase()
        .includes(search)
    );
  });
  const todayStr = toYMD(new Date());
  const [exportModal, setExportModal] = useState(false);
  const [exportFromDate, setExportFromDate] = useState(todayStr);
  const [exportToDate, setExportToDate] = useState(todayStr);
  // applied filters
  const [applied, setApplied] = useState({
    driverCode: "",
    search: "",
    type: "all",
    status: "all",
  });
  // data
  // Per-driver immutable edit map: editing one badge only rerenders that driver.
  const [dirtyByDriver, setDirtyByDriver] = useState({});
  const [hover, setHover] = useState(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(430);
  const gridRef = useRef(null);
  const scrollFrame = useRef(null);
  const latestScrollTop = useRef(0);
  const [attendanceLoading, setAttendanceLoading] = useState(false);
  const [attendanceDrivers, setAttendanceDrivers] = useState([]);
  const navigate = useNavigate();
  const canCallApi = !!AdminId;
  const dropdownRef = useRef(null);

  useEffect(() => {
    const grid = gridRef.current;
    if (!grid) return;
    const measure = () => setViewportHeight(grid.clientHeight || 430);
    measure();
    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(measure)
        : null;
    resizeObserver?.observe(grid);
    window.addEventListener("resize", measure);
    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, []);

  useEffect(
    () => () => {
      if (scrollFrame.current !== null)
        cancelAnimationFrame(scrollFrame.current);
    },
    [],
  );

const handleGridScroll = useCallback((event) => {
  latestScrollTop.current = event.currentTarget.scrollTop;

  if (scrollFrame.current === null) {
    scrollFrame.current = requestAnimationFrame(() => {
      setScrollTop(latestScrollTop.current);
      scrollFrame.current = null;
    });
  }

  setHover(null);
}, []);


const handleOpenEditor = useCallback((driverCode, date, original) => {
  // Hide hover detail while editing
  setHover(null);

  setDirtyByDriver((previous) => {
    const previousDates = previous[driverCode] || EMPTY_DIRTY;

    // Always get latest status
    const currentStatus = previousDates[date] ?? original;

    const currentIndex = STATUS_ORDER.indexOf(currentStatus);

    // Instantly move to next status
    const nextStatus = STATUS_ORDER[(currentIndex + 1) % STATUS_ORDER.length];

    const updatedDates = {
      ...previousDates,
    };

    // If back to original, remove unsaved change
    if (nextStatus === original) {
      delete updatedDates[date];
    } else {
      updatedDates[date] = nextStatus;
    }

    const updated = {
      ...previous,
    };

    if (Object.keys(updatedDates).length > 0) {
      updated[driverCode] = updatedDates;
    } else {
      delete updated[driverCode];
    }

    return updated;
  });
}, []);




const handleBadgeHover = useCallback((event, details, status) => {
  setHover({
    details,
    status,
    position: getPopupPosition(event.currentTarget, 264),
  });
}, []);


  const handleBadgeLeave = useCallback(() => setHover(null), []);

  const handleNavigateDriver = useCallback(
    (d) => {
      const username = encodeURIComponent(d.name || d.driverCode);
      const monthStart = `${year}-${String(month + 1).padStart(2, "0")}-01`;
      const todayDate = toYMD(new Date());
      navigate(`/analytics/${d.driverCode}/${username}`, {
        state: { fromDate: monthStart, toDate: todayDate },
      });
    },
    [navigate, year, month],
  );
  /*  FETCH DRIVER LIST  */
  useEffect(() => {
    if (!AdminId) return;
    fetchDrivers().then((list) => {
      if (Array.isArray(list)) {
        setDriverOptions(list);
      }
    });
  }, [AdminId]);
  const transformDrivers = (apiData = []) => {
    return apiData.map((d) => {
      const attendanceMap = {};
      const attendanceDetailsMap = {};
      (d.attendance || []).forEach((item) => {
        if (item?.date) {
          attendanceMap[item.date] = item.status;
          attendanceDetailsMap[item.date] = {
            date: item.date,
            status: item.status,
            totalDutyHours: item.totalDutyHours || "0h 0m",
            timings: Array.isArray(item.timings) ? item.timings : [],
          };
        }
      });
      return {
        ...d,
        attendanceMap,
        attendanceDetailsMap,
      };
    });
  };
  /*  FETCH ATTENDANCE  */
  const fetchAttendance = async (driverCodeParam) => {
    fetchAttendanceApi(
      {
        driverCode: driverCodeParam || "",
        fromDate: dates[0],
        toDate: dates[dates.length - 1],
      },
      (data) => {
        const formatted = transformDrivers(data || []);
        setAttendanceDrivers(formatted);
      },
      () => setDirtyByDriver({}),
      setAttendanceLoading,
    );
  };
  useEffect(() => {
    if (!AdminId) return;
    if (!dates.length) return;
    fetchAttendance("");
  }, [AdminId, year, month, dates]);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDriverDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);
  /*  APPLY FILTERS  */
  const onApplyFilters = () => {
    const next = {
      driverCode: uiDriverCode,
      search: uiSearch.trim(),
      type: uiType,
      status: uiStatus,
    };
    setApplied(next);
    fetchAttendance(next.driverCode);
  };
  const onResetFilters = () => {
    setUiDriverCode("");
    setUiSearch("");
    setUiType("all");
    setUiStatus("all");
    const next = { driverCode: "", search: "", type: "all", status: "all" };
    setApplied(next);
    fetchAttendance("");
  };
  /* BADGE EDITING: single click opens quick status picker */

  const convertStatusToApi = (status) => {
    if (status === "full_day") return "Full Day";
    if (status === "half_day") return "Half Day";
    if (status === "absent") return "Absent";
    return "P";
  };
  /* SAVE (Batch Update API)  */
  const saveAll = async () => {
    const data = Object.entries(dirtyByDriver).flatMap(
      ([driverCode, changes]) =>
        Object.entries(changes).map(([date, status]) => ({
          driverCode,
          date,
          status: convertStatusToApi(status),
        })),
    );
    if (!data.length) return toast.message("No changes to save");
    if (!canCallApi) return toast.error("Admin/session not ready");
    // Hook expects a clearing callback; never clear edits on a failed save.
    const saved = await updateAttendanceApi(data, () => setDirtyByDriver({}));
    if (saved) await fetchAttendance(applied.driverCode);
  };
  const discardAll = () => {
    if (!Object.keys(dirtyByDriver).length) return;
    setDirtyByDriver({});
    fetchAttendance(applied.driverCode);
  };
  /*  CLIENT-SIDE FILTERING  */
  const filteredDrivers = useMemo(() => {
    let list = [...attendanceDrivers];
    if (uiType !== "all") {
      list = list.filter((d) => String(d.type || "").toLowerCase() === uiType);
    }
    if (uiSearch) {
      const q = uiSearch.toLowerCase();
      list = list.filter((d) => {
        const name = String(d.name || "")
          .toLowerCase()
          .trim();
        const code = String(d.driverCode || "").toLowerCase();
        return name.includes(q) || code.includes(q);
      });
    }
    if (uiStatus !== "all") {
      list = list.filter((d) =>
        Object.values(d.attendanceMap || {}).some(
          (s) => normalizeStatus(s) === uiStatus,
        ),
      );
    }
    return list;
  }, [attendanceDrivers, uiSearch, uiType, uiStatus]);
  const dirtyCount = useMemo(
    () =>
      Object.values(dirtyByDriver).reduce(
        (sum, changes) => sum + Object.keys(changes).length,
        0,
      ),
    [dirtyByDriver],
  );

  // Only render rows visible in the scroll area (+overscan for fluid wheel scrolling).
  const startIndex = Math.min(
    Math.max(0, filteredDrivers.length - 1),
    Math.max(0, Math.floor(scrollTop / ROW_HEIGHT) - OVERSCAN),
  );
  const visibleCount = Math.ceil(viewportHeight / ROW_HEIGHT) + OVERSCAN * 2;
  const endIndex = Math.min(filteredDrivers.length, startIndex + visibleCount);
  const visibleDrivers = filteredDrivers.slice(startIndex, endIndex);
  const topPadding = startIndex * ROW_HEIGHT;
  const bottomPadding = Math.max(
    0,
    (filteredDrivers.length - endIndex) * ROW_HEIGHT,
  );

  useEffect(() => {
    const grid = gridRef.current;
    if (grid) grid.scrollTop = 0;
    setScrollTop(0);
    setHover(null);
  }, [year, month, uiDriverCode, uiSearch, uiType, uiStatus]);
  const handleExportDownload = () => {
    if (!filteredDrivers.length) {
      toast.message("No data to export");
      return;
    }
    const exportData = filteredDrivers.map((d) => {
      const row = {
        DriverCode: d.driverCode,
        Name: d.name || "",
        Type: d.type || "",
      };
      dates
        .filter((date) => date >= exportFromDate && date <= exportToDate)
        .forEach((date) => {
          const status = normalizeStatus(d.attendanceMap?.[date]);
          row[`${formatDay(date)}-${formatDayName(date)}`] =
            STATUS_LABEL[status];
        });
      return row;
    });
    exportAnyJsonToExcel({
      data: exportData,
      filePrefix: `Attendance_${exportFromDate}_to_${exportToDate}`,
      sheetName: "Attendance",
    });
    setExportModal(false);
  };
  return (
    <div className="w-full overflow-x-hidden px-4 py-4">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Attendance</h1>
          <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-slate-600">
            <div className="flex items-center gap-1">
              <span className="att-legend bg-emerald-500" /> F – Full
            </div>
            <div className="flex items-center gap-1">
              <span className="att-legend bg-amber-400" /> H – Half
            </div>
            <div className="flex items-center gap-1">
              <span className="att-legend bg-rose-500" /> A – Absent
            </div>
            <div className="flex items-center gap-1">
              <span className="att-legend bg-indigo-500" /> PN – Pending
            </div>
            <span className="text-slate-400">
              • Click badge to change status (PN → H → F → A → PN)
            </span>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {/* Refresh Button */}
          <button
            onClick={() => fetchAttendance(applied.driverCode)}
            disabled={attendanceLoading}
            className="flex items-center gap-2 px-3 py-2 rounded-xl bg-gray-500 hover:bg-gray-600 text-white"
          >
            <RefreshCcw
              size={14}
              className={attendanceLoading ? "animate-spin" : ""}
            />
            <span>Refresh</span>
          </button>
          {/* Export Button */}
          <button
            onClick={() => {
              setExportFromDate(todayStr);
              setExportToDate(todayStr);
              setExportModal(true);
            }}
            className="flex items-center gap-2 px-3 py-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white"
          >
            <FileDown size={14} />
            <span>Export</span>
          </button>
          <button
            onClick={discardAll}
            disabled={loading || attendanceLoading || dirtyCount === 0}
            className="px-3 py-2 rounded-xl border border-rose-200 bg-white text-rose-700 hover:bg-rose-50 disabled:opacity-60"
          >
            Discard ({dirtyCount})
          </button>
          <button
            onClick={saveAll}
            disabled={loading || attendanceLoading || dirtyCount === 0}
            className="px-3 py-2 rounded-xl bg-green-600 hover:bg-green-700 text-white disabled:opacity-60"
          >
            Save Changes ({dirtyCount})
          </button>
        </div>
      </div>
      {attendanceLoading && (
        <div
          className="mt-3 flex items-center gap-2 text-xs font-semibold text-orange-600"
          role="status"
        >
          <RefreshCcw size={14} className="animate-spin" /> Updating attendance
          data...
        </div>
      )}
      <>
        {/* Filters */}
        <div className="mt-4 bg-white rounded-2xl border border-slate-200 ">
          <div className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
              <div className="md:col-span-2">
                <label className="text-xs text-slate-500">Month</label>
                <input
                  type="month"
                  value={`${year}-${String(month + 1).padStart(2, "0")}`}
                  onChange={(e) => {
                    const [y, m] = e.target.value.split("-");
                    setYear(+y);
                    setMonth(+m - 1);
                  }}
                  className="mt-1 w-full px-3 py-2 rounded-xl border border-slate-200"
                />
              </div>
              <div className="md:col-span-4">
                <label className="text-xs text-slate-500">Driver</label>
                <div ref={dropdownRef} className="relative mt-1">
                  <button
                    type="button"
                    disabled={driverListLoading}
                    onClick={() => {
                      setDriverDropdownOpen((prev) => !prev);
                      setDriverSearch("");
                    }}
                    className="w-full px-3 py-2 rounded-xl border border-slate-200 bg-white text-left flex justify-between items-center"
                  >
                    <span className="truncate">
                      {selectedDriver
                        ? `${selectedDriver.fullName || selectedDriver.username || selectedDriver.driverId} (${selectedDriver.driverId})`
                        : "All Drivers"}
                    </span>
                    <ChevronDown
                      size={18}
                      className={`text-slate-500 transition-transform duration-200 ${
                        driverDropdownOpen ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                  {driverDropdownOpen && (
                    <div className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                      <div className="sticky top-0 bg-white p-2 border-b border-slate-200">
                        <input
                          type="text"
                          value={driverSearch}
                          onChange={(e) => setDriverSearch(e.target.value)}
                          placeholder="Search driver..."
                          className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setUiDriverCode("");
                          setDriverDropdownOpen(false);
                        }}
                        className="w-full text-left px-3 py-2 hover:bg-blue-50"
                      >
                        All Drivers
                      </button>
                      {filteredDriverOptions.map((d) => (
                        <button
                          key={d.driverId}
                          type="button"
                          onClick={() => {
                            setUiDriverCode(d.driverId);
                            setDriverDropdownOpen(false);
                          }}
                          className={`w-full text-left px-3 py-2 hover:bg-blue-50 ${
                            uiDriverCode === d.driverId ? "bg-blue-100" : ""
                          }`}
                        >
                          {d.fullName || d.username || d.driverId} ({d.driverId}
                          )
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="md:col-span-3">
                <label className="text-xs text-slate-500">Search</label>
                <input
                  value={uiSearch}
                  onChange={(e) => {
                    const value = e.target.value;
                    setUiSearch(value);
                    // optional: update applied state too if you still use it elsewhere
                    setApplied((prev) => ({
                      ...prev,
                      search: value.trim(),
                    }));
                  }}
                  placeholder="Search name or code..."
                  className="mt-1 w-full px-3 py-2 rounded-xl border border-slate-200"
                />
              </div>
              <div className="md:col-span-1">
                <label className="text-xs text-slate-500">Type</label>
                <select
                  value={uiType}
                  onChange={(e) => setUiType(e.target.value)}
                  className="mt-1 w-full px-3 py-2 rounded-xl border border-slate-200 bg-white"
                >
                  <option value="all">All</option>
                  <option value="full_time">Full-time</option>
                  <option value="part_time">Part-time</option>
                  <option value="normal">Normal</option>
                </select>
              </div>
              <div className="md:col-span-1">
                <label className="text-xs text-slate-500">Status</label>
                <select
                  value={uiStatus}
                  onChange={(e) => setUiStatus(e.target.value)}
                  className="mt-1 w-full px-3 py-2 rounded-xl border border-slate-200 bg-white"
                >
                  <option value="all">Any</option>
                  <option value="full_day">Full</option>
                  <option value="half_day">Half</option>
                  <option value="absent">Absent</option>
                </select>
              </div>
              <div className="md:col-span-12 flex flex-wrap items-center justify-between gap-2 mt-2">
                <div className="flex gap-2">
                  <button
                    onClick={onApplyFilters}
                    disabled={loading || !canCallApi}
                    className="px-4 py-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-60"
                  >
                    Apply Filters
                  </button>
                  <button
                    onClick={onResetFilters}
                    disabled={loading || !canCallApi}
                    className="px-4 py-2 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-60"
                  >
                    Reset
                  </button>
                </div>
                <div className="text-xs text-slate-500">
                  Showing{" "}
                  <span className="font-semibold text-slate-700">
                    {filteredDrivers.length}
                  </span>{" "}
                  drivers •{" "}
                  <span className="font-semibold text-slate-700">
                    {dates.length}
                  </span>{" "}
                  days
                </div>
              </div>
            </div>
          </div>
          {/* Grid */}
          <div className="bg-white border border-slate-200 mt-4">
            <div
              className="attendance-grid-scroll"
              ref={gridRef}
              onScroll={handleGridScroll}
            >
              <table className="attendance-table">
                <thead>
                  <tr>
                    <th
                      className="attendance-th attendance-driver-col text-left"
                      style={{
                        position: "sticky",
                        top: 0,
                        left: 0,
                        zIndex: 100,
                        background: "#fff",
                      }}
                    >
                      Partner
                    </th>
                    {dates.map((d) => (
                      <th
                        key={d}
                        className="bg-[#80d1e7] attendance-th sticky-top text-center date-col"
                      >
                        <div className="flex flex-col items-center leading-tight">
                          <span className="text-[10px] font-medium">
                            {formatDayName(d)}
                          </span>
                          <span className="text-sm font-bold">
                            {formatDay(d)}
                          </span>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredDrivers.length === 0 && (
                    <tr>
                      <td
                        colSpan={1 + dates.length}
                        className="py-10 text-center text-slate-500"
                      >
                        {attendanceLoading
                          ? "Loading attendance..."
                          : "No drivers found for this filter/month."}
                      </td>
                    </tr>
                  )}
                  {topPadding > 0 && (
                    <tr
                      className="attendance-virtual-spacer"
                      style={{ height: topPadding }}
                    >
                      <td colSpan={1 + dates.length} />
                    </tr>
                  )}
                  {visibleDrivers.map((driver) => (
                    <AttendanceDriverRow
                      key={driver.driverCode}
                      driver={driver}
                      dates={dates}
                      changes={dirtyByDriver[driver.driverCode] || EMPTY_DIRTY}
                      onEdit={handleOpenEditor}
                      onHover={handleBadgeHover}
                      onLeave={handleBadgeLeave}
                      onNavigate={handleNavigateDriver}
                    />
                  ))}
                  {bottomPadding > 0 && (
                    <tr
                      className="attendance-virtual-spacer"
                      style={{ height: bottomPadding }}
                    >
                      <td colSpan={1 + dates.length} />
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          <div className="px-4 py-3 border-t border-slate-200 flex flex-wrap items-center justify-between gap-2">
            <div className="text-xs text-slate-500">
              Driver column is fixed. Scroll inside grid to see all dates.
            </div>
            <div className="text-xs">
              {dirtyCount ? (
                <span className="text-rose-600 font-semibold">
                  {dirtyCount} unsaved change(s)
                </span>
              ) : (
                <span className="text-emerald-600 font-semibold">
                  All changes saved
                </span>
              )}
            </div>
          </div>
        </div>
      </>

      {/* Only hover details, no status selection modal */}
      <AttendanceHoverDetails popup={hover} />

      {exportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-96 space-y-5">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-800">
                Export Attendance
              </h3>
              <button
                onClick={() => setExportModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={20} />
              </button>
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-600">
                From Date
              </label>
              <input
                type="date"
                value={exportFromDate}
                onChange={(e) => setExportFromDate(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-600">
                To Date
              </label>
              <input
                type="date"
                value={exportToDate}
                onChange={(e) => setExportToDate(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
            </div>
            <div className="flex gap-3 pt-2">
              <button
                onClick={() => setExportModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover: transition"
              >
                Cancel
              </button>
              <button
                onClick={handleExportDownload}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm transition"
              >
                <FileDown size={15} />
                Download
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
